import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appAPI } from '../../utils/api';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { getMedicalProfileResourceDescriptionForCategoryCacheKey } from './cacheKeys';

const intl = getIntl('useResourceDescription', {
    resourceDescriptionUpdateError: 'Error fetching resource description',
});

export type UseResourceDescriptionArgs = {
    medicalProfileId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    resourceId: string;
};

export type UseResourceDescriptionReturnValue = {
    description: string;
    isLoadingResourceDescription: boolean;
    fetchResourceDescription: () => void;
};

export const useResourceDescription = (args: UseResourceDescriptionArgs): UseResourceDescriptionReturnValue => {
    const { medicalProfileId, resourceId, resourceCategory } = args;
    const [enabled, setEnabled] = useState(false);

    const resourceDesc = useQuery({
        queryKey: getMedicalProfileResourceDescriptionForCategoryCacheKey(
            medicalProfileId,
            resourceCategory,
            resourceId,
        ),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalDataService.GetResourceDescriptionResponse>(
                urls.api.medicalData.resourceDescription(medicalProfileId, resourceCategory, resourceId),
            );

            return response.description;
        },
        enabled,
    });
    useQueryErrorNotify(resourceDesc, intl('resourceDescriptionUpdateError'));

    const fetchResourceDescription = () => {
        setEnabled(true);
    };

    return {
        description: resourceDesc.data || '',
        isLoadingResourceDescription: resourceDesc.isLoading,
        fetchResourceDescription,
    };
};
